<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Spinner v-if="isLoading"/>
        <div v-if="!isLoading && ticketData" :class="{'datatable-is-loading':ticketIsLoading}">
          <Toolbar class="p-mb-4 p-p-2">
            <template #start>
              <div class="p-p-1">
                <Button class="p-button-text p-button-info p-mr-3"
                        @click="goBack"
                        icon="ti-arrow-left"
                        :label="$t('Go back')"/>
              </div>
              <div style="font-size: 1.4em; color: inherit">
                {{ $t('Ticket') }} <span v-if="ticketData.id">#{{ formatTicketNumber(ticketData.id, ticketData.created_at) }}</span>
              </div>
            </template>

            <template #end v-if="isAdminMode">
              <div class="p-m-1" v-if="canCloseTicket">
                <Button class="p-button-raised"
                        icon="ti-close"
                        :disabled="actionLoading"
                        @click="closeTicket"
                        :label="$t('Close')"/>
              </div>

              <div class="p-m-1" v-else-if="canReopenTicket">
                <Button class="p-button-raised"
                        :disabled="actionLoading"
                        @click="reopenTicket"
                        :label="$t('reopenTicketLabel')"/>
              </div>

              <div class="p-m-1">
                <Button class="p-button-raised p-button-warning"
                        icon="ti-trash"
                        :disabled="actionLoading || !canDeleteTicket"
                        @click="onDeleteTicket"
                        :label="$t('Delete')"/>
              </div>
            </template>
          </Toolbar>

          <div class="p-fieldset p-p-1 p-mt-3 p-mb-4">
            <div class="p-grid p-p-0 p-m-0">
              <div class="p-col-12 p-sm-12 p-md-12 p-lg-12" v-if="$store.state.mobileLayout || $store.state.tabletLayout">
                <table>
                  <tbody style="text-align: left">
                  <tr>
                    <th class="p-p-1">{{ $t('Subject') }}:</th>
                    <td class="p-p-1">
                      <div class="p-ml-3">
                        <template v-if="ticketData.subject">{{ ticketData.subject }}</template>
                        <i v-else class="ti-minus"/>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th class="p-p-1">{{ $t('Type') }}:</th>
                    <td class="p-p-1">
                      <div class="p-ml-3">
                        <template v-if="ticketData.type">{{ formatType(ticketData.type) }}</template>
                        <i v-else class="ti-minus"/>
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <th class="p-p-1">{{ $t('Status') }}:</th>
                    <td class="p-p-1">
                      <div class="p-ml-3">
                        <div class="p-d-flex p-flex-wrap p-ai-center" v-if="resolvedTicketState">
                          <div class="state-badge" :class="resolvedTicketState.color || ''">
                            {{ resolvedTicketState.label?.[$i18n.locale] || resolvedTicketState.label?.en || '' }}
                          </div>
                        </div>
                        <i v-else class="ti-minus"/>
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <th class="p-p-1">{{$t('Created')}}:</th>
                    <td class="p-p-1">
                      <div class="p-ml-3">
                        <template v-if="ticketData.created_at">
                          {{ formatBy(ticketData.created_at, ticketData.creator) }}
                        </template>
                        <i v-else class="ti-minus"/>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <template v-else>
                <div class="p-col-12 p-sm-12 p-md-12 p-lg-6">
                  <table>
                    <tbody style="text-align: left">
                    <tr>
                      <th class="p-p-1">{{ $t('Subject') }}:</th>
                      <td class="p-p-1">
                        <div class="p-ml-3">
                          <template v-if="ticketData.subject">{{ ticketData.subject }}</template>
                          <i v-else class="ti-minus"/>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th class="p-p-1">{{ $t('Type') }}:</th>
                      <td class="p-p-1">
                        <div class="p-ml-3">
                          <template v-if="ticketData.type">{{ formatType(ticketData.type) }}</template>
                          <i v-else class="ti-minus"/>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div class="p-col-12 p-sm-12 p-md-12 p-lg-6">
                  <table>
                    <tbody style="text-align: left">
                    <tr>
                      <th class="p-p-1">{{ $t('Status') }}:</th>
                      <td class="p-p-1">
                        <div class="p-ml-3">
                          <div class="p-d-flex p-flex-wrap p-ai-center" v-if="resolvedTicketState">
                            <div class="state-badge" :class="resolvedTicketState.color || ''">
                              {{ resolvedTicketState.label?.[$i18n.locale] || resolvedTicketState.label?.en || '' }}
                            </div>
                          </div>
                          <i v-else class="ti-minus"/>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th class="p-p-1">{{$t('Created')}}:</th>
                      <td class="p-p-1">
                        <div class="p-ml-3">
                          <template v-if="ticketData.created_at">
                            {{ formatBy(ticketData.created_at, ticketData.creator) }}
                          </template>
                          <i v-else class="ti-minus"/>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>

          <div class="p-fieldset p-p-1 p-mt-3 p-mb-3">
            <div class="p-grid p-p-0 p-m-0">
              <div class="p-col-12 p-sm-12 p-md-12 p-lg-12">
                <div class="panel-body">
                  <div v-if="!messages.length" class="empty-page">{{ $t('No data available') }}</div>

                  <div v-else class="ticket-thread__list">
                    <div v-for="message in messages"
                         :key="message.id"
                         class="ticket-message"
                         :class="messageAlignClass(message)">
                      <div class="ticket-message__bubble">

                        <div class="p-d-flex p-ai-center p-jc-between p-flex-wrap p-mb-1">
                          <div class="ticket-message__meta">
                            <span class="ticket-message__author">
                              {{ messageAuthorName(message) }}
                            </span>
                            <span class="ticket-message__time">
                              {{ formatDate(message.created_at) }}
                            </span>
                          </div>
                          <div v-if="canEditMessage(message)">
                            <Button icon="ti-pencil"
                                    v-tooltip.top="$t('Edit')"
                                    class="p-button-text p-button-sm"
                                    @click="startEditMessage(message)"/>
                          </div>
                        </div>


                        <div v-if="editingMessageId === Number(message.id)" class="p-mt-3">
                          <Textarea v-model="editingMessageText"
                                    rows="4"
                                    class="textarea-vertical-resize"
                                    style="width: 100%;"
                                    :maxlength="maxMessageLength"
                                    :disabled="editingLoading"/>

                          <div class="p-d-flex p-ai-center p-jc-end p-mt-2 p-mb-3">
                            <Button class="p-button-sm p-button-text p-mr-2"
                                    :label="$t('Cancel')"
                                    :disabled="editingLoading"
                                    @click="cancelEditMessage"/>

                            <Button class="p-button-sm"
                                    :label="$t('Save')"
                                    :loading="editingLoading"
                                    :disabled="!editingMessageText.trim() || editingMessageText.length > maxMessageLength"
                                    @click="saveEditedMessage(message)"/>
                          </div>
                        </div>

                        <div v-else class="ticket-message__text">
                          {{ message.message }}
                        </div>

                        <div v-if="message.edited_at" class="ticket-message__edited">
                          <template v-if="Number(message.edited_by) === Number(ticketData.created_by)">
                            {{ $t('editedLabel') }} {{ formatBy(message.edited_at, message.editedBy) }}
                          </template>
                          <template v-else>
                            {{ $t('editedLabel') }} {{ formatDate(message.edited_at) }}, {{ $t('Support') }}
                          </template>
                        </div>

                        <div v-if="message.files?.length" class="p-mt-2">
                          <FilesList :loading="ticketIsLoading"
                                     :canDelete="isAdminMode"
                                     :files="message.files"
                                     :itemId="message.id"
                                     entity="ticket_message"
                                     @update-item="getTicketData"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="p-mt-5 p-mb-4" v-show="!replyBlocked">

                <div class="p-grid p-mb-3" v-show="!replyBlocked">
                  <div class="p-col-12 p-sm-12 p-md-7 p-lg-7">
                    <Textarea v-model="replyMessage"
                              rows="6"
                              style="width: 100%;"
                              :maxlength="maxMessageLength"
                              :placeholder="$t('ticketReplyPlaceholder')"
                              :disabled="replyDisabled"/>

                    <div class="p-d-flex p-ai-center p-jc-end p-mt-2">
                      <Button class="p-button-raised"
                              :disabled="!canSendReply"
                              :loading="sendingReply"
                              icon="pi pi-send"
                              :label="$t('sendReplyLabel')"
                              @click="sendReply"/>
                    </div>
                  </div>

                  <div class="p-col-12 p-sm-12 p-md-5 p-lg-5">
                    <FileUploader :item="{ id: null }"
                                  @select="onFileSelect"
                                  @clear="onFilesClear"
                                  :disabled="replyDisabled"
                                  @remove="onFileRemove"
                                  :showButton="false"
                                  :showRemainingFiles="false"
                                  ref="supportTicketFileUploader"
                                  field="support-ticket-files"
                                  :multiple="true"
                                  :allowNonImages="true"
                                  :imagesOnly="false"
                                  :itemWidth="90"
                                  :itemHeight="90"
                                  :maxFiles="5"
                                  :maxFilesPerUpload="5"
                                  :maxSizeMb="10"
                                  :maxWidth="1600"
                                  :maxHeight="1600"
                                  :jpegQuality="0.82"
                                  resizeMode="contain"/>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
        <div v-else-if="!isLoading && !ticketIsLoading && !ticketData" class="empty-page">{{ $t('No data available') }}</div>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteTicket"/>
      </div>
    </div>
  </div>
</template>

<script>
import settings from "@/settings";
import httpClient from "@/services/http.services";
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import TicketStates from "@/translations/states/TicketStates";
import FileUploader from "@/components/FileUploader.vue";
import FilesList from "@/components/FilesList.vue";

export default {
  components: { FileUploader, FilesList },
  mixins: [ datatableMixins, formatMixins ],
  name: 'SupportTicketView',
  data() {
    return {
      actionLoading: false,
      attachments: [],
      ticketIsLoading: false,
      sendingReply: false,
      isLoading: true,
      ticketData: null,
      messages: [],
      replyMessage: '',
      maxMessageLength: 2000,
      TicketStates,

      TYPE_QUESTION: 1,
      TYPE_BUG: 3,
      TYPE_IDEA: 5,
      TYPE_FEATURE: 7,

      STATE_OPEN: 1,
      STATE_ANSWERED: 3,

      STATUS_ACTIVE: 1,
      STATUS_CLOSED: 2,

      SENDER_TYPE_CUSTOMER: 1,
      SENDER_TYPE_STAFF: 3,

      editingMessageId: null,
      editingMessageText: '',
      editingLoading: false,
    }
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.getPageData()
  },
  methods: {
    onDeleteTicket() {
      this.itemToDelete = {...this.ticketData}
      this.confirmItemDeleteModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    getPageData() {
      if (!this.$route.params.id) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getTicketData()
    },
    goBack() {
      if (this.isAdminMode) {
        this.$router.push('/admin/support/tickets')
      } else {
        this.$router.push('/tickets')
      }
    },
    clearAttachments() {
      this.attachments = []
      this.$refs.supportTicketFileUploader?.clearAttachments()
    },
    async getTicketData(showSpinner = true) {
      if (!this.$route.params.id) return
      if (!this.userCanGetData || this.ticketIsLoading) return
      this.isLoading = showSpinner
      this.disableEditButton = true
      this.ticketIsLoading = true
      const ctrl = this.setController?.('userTicketData')
      try {
        const { status, data } = await httpClient({
          url: 'ticket/view-ticket',
          method: 'post',
          params: {
            expand: 'creator,files,editedBy'
          },
          data: { id: this.$route.params.id },
          signal: ctrl?.signal
        })

        if (status === 200 && data && data.success && data.ticket) {
          if (this.isAdminMode) {
            this.$store.commit('updateUnansweredTicketsCount', data.unanswered_tickets_count ?? 0)
          } else {
            this.$store.commit('updateAnsweredTicketsCount', data.answered_tickets_count ?? 0)
          }

          this.ticketData = data.ticket
          this.messages = Array.isArray(data.messages) ? data.messages : []

          if (this.editingMessageId) {
            const editingMessageStillExists = this.messages.find(m => Number(m.id) === Number(this.editingMessageId))
            if (!editingMessageStillExists) {
              this.cancelEditMessage()
            }
          }
        } else {
          this.showUnknownError()
          this.ticketData = null
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.ticketIsLoading = false
        this.isLoading = false
        this.disableEditButton = false
        if (ctrl && this._abortors?.userTicketData === ctrl) {
          this._abortors.userTicketData = null
        }
      }
    },

    async sendReply() {
      if (!this.canSendReply || !this.ticketData?.id) return false

      this.sendingReply = true

      try {
        const formData = new FormData()
        formData.append('id', this.ticketData.id)
        formData.append('message', this.replyMessage.trim())

        for (let i = 0; i < (this.attachments?.length || 0); i++) {
          formData.append(i, this.attachments[i])
        }

        const { status, data } = await httpClient.post(`ticket/reply-ticket`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (!(status === 200 && data?.success)) {
          if (data?.error) {
            this.$toast.add({
              severity: 'error',
              summary: data.error.summary,
              detail: data.error.detail,
              life: settings.toastLife
            })
            return false
          }

          this.showUnknownError()
          return false
        }

        this.replyMessage = ''
        this.clearAttachments()
        await this.getTicketData(false)

        this.$toast.add({
          severity: 'success',
          detail: this.$t('messageSentMsg'),
          life: settings.toastLife
        })

        return true
      } catch (err) {
        this.showError(err)
        return false
      } finally {
        this.sendingReply = false
      }
    },

    async closeTicket() {
      const ticketId = this.ticketData?.id
      if (!ticketId) return

      this.actionLoading = true

      try {
        const { data, status } = await httpClient.post('ticket/close-ticket', {
          id: ticketId,
        })

        if ((status === 200 || status === 201) && data?.success) {
          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data updated'),
            life: settings.toastLife
          })
          await this.getTicketData()
          return
        }

        if (data?.error) {
          this.$toast.add({
            severity: 'error',
            summary: data.error.summary,
            detail: data.error.detail || this.$t('Unknown error'),
            life: settings.toastLife
          })
          return
        }

        this.showUnknownError()
      } catch (e) {
        this.showError(e)
      } finally {
        this.actionLoading = false
      }
    },

    async reopenTicket() {
      const ticketId = this.ticketData?.id
      if (!ticketId) return

      this.actionLoading = true

      try {
        const { data, status } = await httpClient.post('ticket/reopen-ticket', {
          id: ticketId,
        })

        if ((status === 200 || status === 201) && data?.success) {
          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data updated'),
            life: settings.toastLife
          })
          await this.getTicketData()
          return
        }

        if (data?.error) {
          this.$toast.add({
            severity: 'error',
            summary: data.error.summary,
            detail: data.error.detail || this.$t('Unknown error'),
            life: settings.toastLife
          })
          return
        }

        this.showUnknownError()
      } catch (e) {
        this.showError(e)
      } finally {
        this.actionLoading = false
      }
    },

    async deleteTicket() {
      const ticketId = this.ticketData?.id
      if (!ticketId || !this.$store.getters.userCanGetData) return

      this.actionLoading = true

      try {
        const { data, status } = await httpClient.post('ticket/delete-ticket', {
          id: ticketId,
        })

        if ((status === 200 || status === 201) && data?.success) {
          if (this.isAdminMode) {
            this.$store.commit('updateUnansweredTicketsCount', data.unanswered_tickets_count ?? 0)
          } else {
            this.$store.commit('updateAnsweredTicketsCount', data.answered_tickets_count ?? 0)
          }

          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data deleted'),
            life: settings.toastLife
          })
          this.goBack()
          return
        }

        if (data?.error) {
          this.$toast.add({
            severity: 'error',
            summary: data.error.summary,
            detail: data.error.detail || this.$t('Unknown error'),
            life: settings.toastLife
          })
          return
        }

        this.showUnknownError()
      } catch (e) {
        this.showError(e)
      } finally {
        this.actionLoading = false
      }
    },

    async saveEditedMessage(message) {
      if (!message?.id || !this.editingMessageText.trim()) return false

      this.editingLoading = true

      try {
        const { status, data } = await httpClient.post('ticket/update-message', {
          id: Number(message.id),
          message: this.editingMessageText.trim(),
        })

        if (status === 200 && data?.success) {
          await this.getTicketData(false)
          this.cancelEditMessage()

          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data updated'),
            life: settings.toastLife
          })

          return true
        }

        if (data?.error) {
          this.$toast.add({
            severity: 'error',
            summary: data.error.summary,
            detail: data.error.detail,
            life: settings.toastLife
          })
          return false
        }

        this.showUnknownError()
        return false
      } catch (err) {
        this.showError(err)
        return false
      } finally {
        this.editingLoading = false
      }
    },

    startEditMessage(message) {
      if (!this.canEditMessage(message)) return

      this.editingMessageId = Number(message.id)
      this.editingMessageText = message.message || ''
    },

    cancelEditMessage() {
      this.editingMessageId = null
      this.editingMessageText = ''
    },

    formatTicketNumber(id, createdAt = null) {
      const numericId = Number(id) || 0
      const year = createdAt
          ? new Date(Number(createdAt) * 1000).getFullYear()
          : new Date().getFullYear()

      return `VG-${year}-${String(numericId).padStart(4, '0')}`
    },

    onFileSelect(data) {
      this.attachments = data.files || []
    },
    onFilesClear() {
      this.attachments = []
    },
    onFileRemove(data) {
      this.attachments = data?.files || []
    },
    formatType(type) {
      switch (Number(type)) {
        case 1:
          return this.$t('Question')
        case 3:
          return this.$t('Bug')
        case 5:
          return this.$t('Idea')
        case 7:
          return this.$t('Feature')
        default:
          return type
      }
    },
    creatorName(creator) {
      if (!creator) return '—'

      const fullName = [creator.first_name, creator.last_name]
          .filter(Boolean)
          .join(' ')
          .trim()

      if (fullName) return fullName
      if (creator.company_name) return creator.company_name

      return `#${creator.id}`
    },

    messageAuthorName(message) {
      if (Number(message.sender_type) === this.SENDER_TYPE_STAFF) {
        return this.$t('Support')
      }

      if (message.creator) {
        return this.creatorName(message.creator)
      }

      return this.$t('User')
    },

    messageAlignClass(message) {
      return Number(message.sender_type) === this.SENDER_TYPE_STAFF
          ? 'ticket-message--support'
          : 'ticket-message--user'
    },
  },
  computed: {
    replyBlocked() {
      return (
          Number(this.ticketData?.status) === this.STATUS_CLOSED ||
          Number(this.ticketData?.is_deleted_by_user) === 1
      )
    },
    replyDisabled() {
      return this.replyBlocked || this.sendingReply
    },
    resolvedTicketState() {
      if (!this.ticketData) return null

      if (Number(this.ticketData.is_deleted_by_user) === 1) {
        return this.TicketStates.find(s => s.id === 7) || null
      }

      if (Number(this.ticketData.status) === this.STATUS_CLOSED) {
        return this.TicketStates.find(s => s.id === 5) || null
      }

      return this.TicketStates.find(s => s.id === Number(this.ticketData.state)) || null
    },

    canEditMessage() {
      return (message) => {
        if (!message || !this.ticketData || this.editingLoading || this.actionLoading) return false

        if (
            Number(this.ticketData.status) === this.STATUS_CLOSED ||
            Number(this.ticketData.is_deleted_by_user) === 1
        ) {
          return false
        }

        const currentUserId = Number(this.$store.getters.userData?.id || 0)
        const messageCreatedBy = Number(message.created_by || 0)


        if (!currentUserId || messageCreatedBy !== currentUserId) return false

        if (this.isAdminMode) {
          const supportMessages = this.messages.filter(
              m => Number(m.sender_type) === this.SENDER_TYPE_STAFF
          )

          if (!supportMessages.length) return false

          const lastSupportMessage = supportMessages[supportMessages.length - 1]
          return Number(lastSupportMessage.id) === Number(message.id)
        }

        if (!this.messages.length) return false

        const lastMessage = this.messages[this.messages.length - 1]
        return Number(lastMessage.id) === Number(message.id)
      }
    },

    isAdminMode() {
      return this.$route.meta.context === 'system'
    },

    replyMessageLength() {
      return (this.replyMessage || '').length
    },

    canSendReply() {
      return (
          !this.sendingReply &&
          !!this.ticketData &&
          Number(this.ticketData.status) !== this.STATUS_CLOSED &&
          Number(this.ticketData.is_deleted_by_user) !== 1 &&
          !!this.replyMessage.trim() &&
          this.replyMessageLength <= this.maxMessageLength
      )
    },

    canDeleteTicket() {
      return !!this.ticketData && !this.actionLoading
    },

    canCloseTicket() {
      return !!this.ticketData &&
          Number(this.ticketData.status) !== this.STATUS_CLOSED &&
          Number(this.ticketData.is_deleted_by_user) !== 1
    },

    canReopenTicket() {
      return !!this.ticketData &&
          Number(this.ticketData.status) === this.STATUS_CLOSED &&
          Number(this.ticketData.is_deleted_by_user) !== 1
    },
  }
}
</script>

<style scoped lang="scss">
.ticket-subject {
  font-size: 1.4em;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ticket-thread__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ticket-message {
  display: flex;
}

.ticket-message--user {
  justify-content: flex-start;
}

.ticket-message--support {
  justify-content: flex-end;
}

.ticket-message__bubble {
  position: relative;
  min-width: 180px;
  max-width: 580px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 8px;
  background: var(--st-message-user-bg);
  border: 1px solid var(--st-message-user-border);
  color: var(--text-color);
}

.ticket-message--support .ticket-message__bubble {
  background: var(--st-message-support-bg);
  border-color: var(--st-message-support-border);
}

.ticket-message__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
  color: var(--muted-text-color);
}

.ticket-message__author {
  font-weight: 500;
  color: var(--text-color);
}

.ticket-message__text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}


.ticket-message__edited {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted-text-color);
  text-align: right;
}
</style>